/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/buttons/styles.css';
import { type ButtonColor, type ButtonSize, type ButtonVariant, toButtonColor } from 'BomdisoftComponents/buttons/render';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { OpenInNewRoundedIcon } from 'BomdisoftComponents/icons/open-in-new-rounded';
import type { ComponentProps, ReactNode } from 'react';

type LinkButtonBase = ComponentProps<'a'> & {
  href: string;
  endIcon?: ReactNode;
  /** A destination outside this product: opens in a new tab with the
   * conventional trailing open-in-new glyph, matching the account menu. */
  external?: boolean;
  fullWidth?: boolean;
  size?: ButtonSize;
  startIcon?: ReactNode;
};

/** Same rule as `Button`: `outlined` renders neutral in every state, so it takes
 *  no tone. Both render through the same stylesheet, so a link that accepted one
 *  would carry the identical half-honoured focus ring. */
export type LinkButtonProps = LinkButtonBase & { color?: ButtonColor; variant?: ButtonVariant };

export const LinkButton = ({ children, className, color, endIcon, external, fullWidth, size = 'medium', startIcon, variant = 'text', ...rest }: LinkButtonProps) => (
  <a className={joinClasses('bomdisoft-button', className)} data-color={toButtonColor(color, variant)} data-full-width={fullWidth ? '' : undefined} data-size={size} data-variant={variant} rel={external ? 'noopener noreferrer' : undefined} target={external ? '_blank' : undefined} {...rest}>
    {startIcon && <span className='bomdisoft-button-start-icon'>{startIcon}</span>}
    {children}
    {external ? <OpenInNewRoundedIcon size={16} /> : endIcon && <span className='bomdisoft-button-end-icon'>{endIcon}</span>}
  </a>
);
